import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Button, Div, FormItem, Flex, TagsTextArea } from 'ui';
import ContentEditable from 'react-contenteditable';
import moment from 'utils/cops-moment';
import { FormattedMessage } from 'react-intl';

const CommentBlock = styled.div`
  font-size: 16px;
`;

const StyledComment = styled(ContentEditable)`
  a:visited,
  a:hover {
    color: #108ee996;
  }
`;

const CommentInfo = styled.div`
  font-size: 12px;
  color: rgba(100, 116, 124, 0.85);
  margin-bottom: 5px;
`;

const CommentUser = styled.div`
  display: inline;
  font-weight: bold;
  font-size: 16px;
`;

const CommentIcon = styled.i`
  color: #108ee996;
  margin-right: 5px;
`;

const StyledFilterFlex = styled(Flex)`
  margin: 10px 0px;
  color: rgba(100, 116, 124, 0.85);
  height: 34px;
`;

const FilterItem = styled.div`
  cursor: pointer;
  padding: 3px 5px;
  margin-right: 5px;
  border-radius: 15px;
  &:hover {
    ${p => !p.isSelected && `
      background-color: lightgray;
    `}
  }
  ${p => p.isSelected && `
    background-color: gray;
    color: white;
  `}
`;

const FilterText = styled.div`
  font-weight: bold;
  padding: 5px;
  margin-right: 5px;
`;

const ExistingComment = ({
  comment: { message, senderDisplayName, dateTimeCreated, systemComment },
}) =>
  <Div marginBottom={25}>
    <CommentInfo>
      {!systemComment ?
        <>
          <CommentUser>
            <CommentIcon className='far fa-comment' />
            {senderDisplayName}
          </CommentUser>{' '}
          <FormattedMessage id='ticket.COMMENTED' />
          {' '}
          {`${moment.utc(dateTimeCreated).from(moment())} (${moment(dateTimeCreated).format(
            'LL - HH:mm',
          )})`}
        </> :
        <>
          <CommentUser>
            <CommentIcon className='fas fa-robot' />
            {senderDisplayName}
          </CommentUser>{' '}
          {message}
        </>
      }
    </CommentInfo>
    {!systemComment ?
      <StyledComment html={message} disabled={true} /> :
      <CommentInfo>
        {`${moment.utc(dateTimeCreated).from(moment())} (${moment(dateTimeCreated).format(
          'LL - HH:mm',
        )})`}
      </CommentInfo>
    }
  </Div>;
ExistingComment.propTypes = {
  comment: PropTypes.shape({
    message: PropTypes.string,
    senderDisplayName: PropTypes.string,
    dateTimeCreated: PropTypes.date,
    systemComment: PropTypes.bool,
  }),
};

const Comments = ({
  addComment,
  ticketId,
  fullPage,
  setFormDirty,
  comments = [],
  tags = [],
  isLoading,
  disabled,
  intl,
  resetComment,
  addLinkedObject,
  ticketWillSave,
  needsFiltering = false,
}) => {
  const [newCommentValue, setNewCommentValue] = useState('');
  const [wasLoading, setWasLoading] = useState(false);
  const [prevTicketWillSave, setPrevTicketWillSave] = useState(false);
  const [filteredComments, setFilteredComments] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState('comments');

  const handleAddComment = () => {
    if (newCommentValue !== '') {
      addComment({
        ticketId,
        comment: newCommentValue.replace(/<div>/gi, '<br>').replace(/<\/div>/gi, ''),
      });
    }
  };

  useEffect(() => {
    setFilteredComments(comments?.filter(c => !c.systemComment));
  }, []);

  useEffect(() => {
    if (comments?.length) {
      setFilteredComments(comments?.filter((c) => {
        if (selectedFilter === 'comments') {
          return !c.systemComment;
        } else if (selectedFilter === 'changes') {
          return c.systemComment;
        }
        return true;
      }));
    }
  }, [comments]);

  useEffect(() => {
    if (!prevTicketWillSave && ticketWillSave) {
      handleAddComment();
    }
    setPrevTicketWillSave(ticketWillSave);
  }, [ticketWillSave]);

  useEffect(() => {
    if (wasLoading && !isLoading) {
      setNewCommentValue('');
    }
    setWasLoading(isLoading);
  }, [isLoading]);

  const handleChangeCommentValue = (v) => {
    setNewCommentValue(v);
    if (fullPage) {
      setFormDirty();
    }
  };

  const resetCommentValue = () => {
    setNewCommentValue('');
  };

  const handleFiltering = (systemComment) => {
    setSelectedFilter(systemComment);
    if (systemComment === 'all') {
      setFilteredComments(comments);
    } else if (systemComment === 'comments') {
      setFilteredComments(comments?.filter(c => !c.systemComment));
    } else if (systemComment === 'changes') {
      setFilteredComments(comments?.filter(c => c.systemComment));
    }
  };

  useEffect(() => {
    if (resetComment) {
      resetCommentValue();
    }
  }, [resetComment]);

  return (
    <CommentBlock>
      <Div marginBottom={12}>
        <FormItem>
          <Div marginBottom={12}>
            <TagsTextArea
              tags={tags}
              value={newCommentValue}
              onChange={handleChangeCommentValue}
              disabled={isLoading || disabled}
              fullPage={fullPage}
              addLinkedObject={addLinkedObject}
              placeholder={intl.formatMessage({ id: 'ticket.COMMENT_PLACEHOLDER' })}
              hideOverflow={true}
              height='70px'
              autoHeight={true}
            />
          </Div>
          <Div marginBottom={12}>
            <Button
              loading={isLoading}
              disabled={disabled || newCommentValue === ''}
              onClick={handleAddComment}
              type='secondary'
            >
              <FormattedMessage id='ticket.COMMENT_BUTTON' />
            </Button>
          </Div>
        </FormItem>
      </Div>
      {needsFiltering ?
        <StyledFilterFlex ffrn aic>
          <FilterText><FormattedMessage id='ticket.SHOW' />{':'}</FilterText>
          <FilterItem
            onClick={() => handleFiltering('comments')}
            isSelected={selectedFilter === 'comments'}
          >
            <FormattedMessage id='ticket.COMMENTS' />
          </FilterItem>
          <FilterItem
            onClick={() => handleFiltering('changes')}
            isSelected={selectedFilter === 'changes'}
          >
            <FormattedMessage id='ticket.CHANGES' />
          </FilterItem>
          <FilterItem
            onClick={() => handleFiltering('all')}
            isSelected={selectedFilter === 'all'}
          >
            <FormattedMessage id='ticket.BOTH' />
          </FilterItem>
        </StyledFilterFlex> :
        null
      }
      {filteredComments &&
        filteredComments
          ?.sort((a, b) => b.id - a.id)
          ?.map(c => <ExistingComment key={c.id} comment={c} fullPage={fullPage} />)}
    </CommentBlock>
  );
};

Comments.propTypes = {
  addComment: PropTypes.func,
  ticketWillSave: PropTypes.bool,
  ticketId: PropTypes.string,
  comments: PropTypes.array,
  tags: PropTypes.array,
  isLoading: PropTypes.bool,
  disabled: PropTypes.bool,
  intl: PropTypes.object,
  fullPage: PropTypes.bool,
  setFormDirty: PropTypes.func,
  resetComment: PropTypes.bool,
  addLinkedObject: PropTypes.func,
  needsFiltering: PropTypes.bool,
};
export default Comments;
